import { Button } from "@/components/ui/button";
import { Play, Lock, CheckCircle2, Sparkles } from "lucide-react";

/**
 * Subject-Overview "Practice / Challenge" dual-pill display.
 *
 * Backend feeds two summaries per sub-topic:
 *   pool_a: { status: "not_started" | "in_progress" | "established", total, correct }
 *   pool_b: { status: "frozen" | "available" | "in_progress" | "established", total, correct }
 *
 * Behaviour (learner-facing — labels are ALWAYS "Practice" / "Challenge"):
 *  - For every non-hard-locked sub-topic BOTH buttons are always rendered. They
 *    never disappear on re-attempt.
 *  - Practice is always tappable (unless hard_locked).
 *  - Challenge is FROZEN (lock icon + "Finish Practice first" hint) until
 *    Pool A is established. Once Pool A establishes, Challenge unfreezes.
 *  - Completion: Practice shows ✓ when pool_a is established; Challenge shows
 *    ✓ when pool_b is established. Both ticks remain permanently after.
 *  - The full-screen "Practice complete!" celebration card fires once,
 *    mid-session, from the engine — this component never renders it.
 *  - hard_locked: parent draws the lock UI; we render nothing.
 */
export default function PoolDualButtons({ subTopic, pool_a, pool_b, lockState, disabled, onStart, practiceLocked }) {
  if (lockState === "hard_locked") return null;

  const a = pool_a || { status: "not_started", total: 0, correct: 0 };
  const b = pool_b || { status: "frozen", total: 0, correct: 0 };

  const challengeFrozen = b.status === "frozen" || practiceLocked;
  const aDone           = a.status === "established";
  const bDone           = b.status === "established";

  return (
    <div
      className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-start"
      data-testid={`pool-buttons-${subTopic}`}
    >
      {/* ── Practice (Pool A) — always shown ─────────────────────────────── */}
      <div className="flex flex-col gap-1" data-testid={`practice-block-${subTopic}`}>
        <Button
          onClick={() => !disabled && !practiceLocked && onStart("A")}
          disabled={disabled || practiceLocked}
          className={`rounded-full text-white px-5 py-4 tap-44 ${
            practiceLocked
              ? "bg-slate-200 text-slate-400 cursor-not-allowed hover:bg-slate-200"
              : aDone
                ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                : "bg-sky-500 hover:bg-sky-600 text-white"
          }`}
          data-testid={`practice-btn-${subTopic}`}
          data-locked={practiceLocked ? "true" : "false"}
        >
          {practiceLocked
            ? <Lock className="w-4 h-4 mr-1.5" />
            : aDone
              ? <CheckCircle2 className="w-4 h-4 mr-1.5" />
              : <Play className="w-4 h-4 mr-1.5" />}
          Practice
          {!practiceLocked && a.total > 0 && (
            <span className="ml-2 text-xs font-bold opacity-90" data-testid={`practice-count-${subTopic}`}>
              {a.correct}/{a.total}
            </span>
          )}
        </Button>
        {/* Unlock hint when practice is teacher-locked after STOP */}
        {practiceLocked && (
          <p
            className="text-[11px] text-slate-500 text-center"
            data-testid={`practice-locked-hint-${subTopic}`}
          >
            Teacher unlock required.
          </p>
        )}
        {/* Hint shown only when Challenge is still frozen and practice is active */}
        {!practiceLocked && challengeFrozen && (
          <p
            className="text-[11px] text-slate-500 text-center"
            data-testid={`practice-unlock-hint-${subTopic}`}
          >
            Complete to unlock Challenge
          </p>
        )}
      </div>

      {/* ── Challenge (Pool B) — always shown, frozen until Pool A done ─── */}
      <div className="flex flex-col gap-1" data-testid={`challenge-block-${subTopic}`}>
        <Button
          onClick={() => !challengeFrozen && !disabled && onStart("B")}
          disabled={challengeFrozen || disabled}
          className={`rounded-full px-5 py-4 tap-44 ${
            challengeFrozen
              ? "bg-slate-200 text-slate-400 cursor-not-allowed hover:bg-slate-200"
              : bDone
                ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                : "bg-violet-500 hover:bg-violet-600 text-white"
          }`}
          data-testid={`challenge-btn-${subTopic}`}
          data-frozen={challengeFrozen ? "true" : "false"}
        >
          {challengeFrozen
            ? <Lock className="w-4 h-4 mr-1.5" />
            : bDone
              ? <CheckCircle2 className="w-4 h-4 mr-1.5" />
              : <Sparkles className="w-4 h-4 mr-1.5" />}
          Challenge
          {!challengeFrozen && b.total > 0 && (
            <span className="ml-2 text-xs font-bold opacity-90" data-testid={`challenge-count-${subTopic}`}>
              {b.correct}/{b.total}
            </span>
          )}
        </Button>
        {challengeFrozen && (
          <p
            className="text-[11px] text-slate-500 text-center"
            data-testid={`challenge-frozen-hint-${subTopic}`}
          >
            Finish Practice first
          </p>
        )}
      </div>
    </div>
  );
}
